/* -------------------------- Design imports start -------------------------- */
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React from 'react'

import CheckboxInput from '../../../components/inputs/CheckboxInput'
import CheckedTextInput from '../../../components/inputs/CheckedTextInput'
import ColorInput from '../../../components/inputs/ColorInput'
import DateInput from '../../../components/inputs/DateInput'
import EmailInput from '../../../components/inputs/EmailInput'
import NumberInput from '../../../components/inputs/NumberInput'
import NumberInputWithUnit from '../../../components/inputs/NumberInputWithUnit'
import OptionalTextInput from '../../../components/inputs/OptionalTextInput'
import { S3FileUpload } from '../../Filesystem'
import SearchSelectInput from '../../../components/inputs/SearchSelectInput'
import SearchMultiSelectInput from '../../../components/inputs/SearchSelectMultiInput'
import SelectWithTextInput from '../../../components/inputs/SelectWithTextInput'
import TextInput from '../../../components/inputs/TextInput'
import TimeDeltaInput from '../../../components/inputs/TimeDeltaInput'
import TimeInput from '../../../components/inputs/TimeInput'
import QuantityMultiInput from '../../../components/inputs/QuantityMultiInput'
import { type FieldObject } from '../utils/types'
import { useFormFieldAutocomplete } from '../useFormFieldAutocomplete'
import UrlInput from '../../../components/inputs/UrlInput'
import { Typography } from '@mui/material'
/* ------------------------- Functional imports end ------------------------- */

interface Props {
  field: FieldObject
  disabled?: boolean
  onChange: (value: any) => void
  value: any
  required?: boolean
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export const FormField = ({ field, onChange, value, disabled, required }: Props) => {
  const inputId = `input-${field.key}`

  // Initialize autocomplete hook - always call to comply with Rules of Hooks
  // Pass empty string if no autocompleteField, hook will return null
  const autocompleteHook = useFormFieldAutocomplete((field as any).autocompleteField || '')

  switch (field.type) {
    case 'custom':
      return <div>{field.CustomComponent}</div>
    case 'email':
      return (
        <EmailInput
          id={inputId}
          label={field.label}
          onChange={onChange}
          value={value}
          disabled={disabled || field.disabled}
          errorMessage={field.errorMessage}
          labelWidth={field.labelWidth}
          validation={field.validation}
          required={required}
          showError={field.showError}
        />
      )
    case 'selectWithFreeText':
      return (
        <SelectWithTextInput
          id={inputId}
          name={field.label}
          value={value}
          options={field.options}
          onChange={onChange}
          freeSolo={true}
          disabled={disabled}
          required={required}
          validation={field.validation}
        />
      )
    case 'select-search':
      return (
        <SearchSelectInput
          id={inputId}
          name={field.label}
          value={value}
          options={field.options}
          loadData={field.loadData}
          loadAllData={field.loadAllData}
          onChange={onChange}
          disabled={disabled || field.disabled}
          errorMessage={field.errorMessage}
          validation={field.validation}
          required={required}
          onAddOption={field.onAddOption}
          useAutocomplete={!!autocompleteHook}
          autocompleteHook={autocompleteHook || undefined}
        />
      )
    case 'url':
      return (
        <UrlInput
          id={inputId}
          label={field.label}
          onChange={onChange}
          value={value}
          disabled={disabled || field.disabled}
          errorMessage={field.errorMessage}
          labelWidth={field.labelWidth}
          validation={field.validation}
          required={required}
          showError={field.showError}
        />
      )
    case 'select-search-multi':
      return (
        <SearchMultiSelectInput
          id={inputId}
          name={field.label}
          value={value}
          options={field.options}
          loadData={field.loadData}
          loadAllData={field.loadAllData}
          onChange={onChange}
          disabled={disabled}
          errorMessage={field.errorMessage}
          validation={field.validation}
          required={required}
          onAddOption={field.onAddOption}
          useAutocomplete={!!autocompleteHook}
          autocompleteHook={autocompleteHook || undefined}
        />
      )
    case 'text':
      return (
        <TextInput
          id={inputId}
          label={field.label}
          onChange={onChange}
          value={value}
          disabled={disabled || field.disabled}
          errorMessage={field.errorMessage}
          labelWidth={field.labelWidth}
          validation={field.validation}
          required={required}
          showError={field.showError}
          loadSuggestions={field.loadSuggestions}
          suggestions={field.suggestions}
          useAutocomplete={!!autocompleteHook}
          autocompleteHook={autocompleteHook || undefined}
        />
      )
    case 'optional-text':
      return (
        <OptionalTextInput
          id={inputId}
          label={field.label}
          onChange={onChange}
          value={value}
          disabled={disabled}
          errorMessage={field.errorMessage}
          labelWidth={field.labelWidth}
          validation={field.validation}
          required={required}
          showError={field.showError}
        />
      )
    case 'multiline':
      return (
        <TextInput
          id={inputId}
          label={field.label}
          onChange={onChange}
          value={value}
          disabled={disabled || field.disabled}
          errorMessage={field.errorMessage}
          labelWidth={field.labelWidth}
          validation={field.validation}
          multiline={true}
          required={required}
          showError={field.showError}
        />
      )
    case 'number':
      return (
        <NumberInput
          id={inputId}
          label={field.label}
          value={value}
          onChange={onChange}
          limits={field.limits}
          disabled={disabled}
          helperText={field.helperText}
          errorMessage={field.errorMessage}
          labelWidth={field.labelWidth}
          validation={field.validation}
          required={required}
          showError={field.showError}
        />
      )
    case 'quantity-multi':
      return (
        <QuantityMultiInput
          id={inputId}
          label={field.label}
          value={value}
          onChange={onChange}
          disabled={disabled}
          errorMessage={field.errorMessage}
          validation={field.validation}
          required={required}
          showError={field.showError}
        />
      )
    case 'measurement': {
      // Extract value and unit from formObject
      // Value can be: string/number (legacy) or { value: string/number, unit: string } (new format)
      const measurementValue = 
        typeof value === 'object' && value !== null && 'value' in value
          ? String(value.value ?? '')
          : String(value ?? '')
      
      const measurementUnit = 
        typeof value === 'object' && value !== null && 'unit' in value && typeof value.unit === 'string'
          ? value.unit
          : field.unit
      const measurementLimits = {
        minInput: 0.000000000000000001,
        ...(field.limits ?? {}),
      }

      const handleValueChange = (input_value: number | string) => {
        const newValue = !Number.isNaN(input_value) ? input_value : ''
        // If unitOptions exist, store as object with value and unit
        if (field.unitOptions && field.unitOptions.length > 0) {
          onChange({ value: newValue, unit: measurementUnit })
        } else {
          // Legacy: just store the value
          onChange(newValue)
        }
      }

      const handleUnitChange = (newUnit: string) => {
        // Update the unit while keeping the current value
        const currentValue = 
          typeof value === 'object' && value !== null && 'value' in value
            ? value.value
            : value
        onChange({ value: currentValue ?? '', unit: newUnit })
      }

      return (
        <NumberInputWithUnit
          id={inputId}
          unit={field.unit}
          currentUnit={measurementUnit}
          label={field.label}
          value={measurementValue}
          limits={measurementLimits}
          onChange={handleValueChange}
          onUnitChange={field.unitOptions && field.unitOptions.length > 0 ? handleUnitChange : undefined}
          unitOptions={field.unitOptions}
          disabled={disabled}
          required={field.required}
          errorMessage={field.errorMessage}
          labelWidth={field.labelWidth}
          validation={true}
        />
      )
    }
    case 'timedelta':
      return (
        <TimeDeltaInput
          id={inputId}
          label={field.label}
          value={value}
          onChange={onChange}
          disabled={disabled}
          required={required}
        />
      )
    case 'time':
      return (
        <TimeInput
          id={inputId}
          label={field.label}
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
      )
    case 'date':
      return (
        <DateInput
          id={inputId}
          value={value}
          label={field.label}
          onChange={onChange}
          disabled={disabled}
          errorMessage={field.errorMessage}
          labelWidth={field.labelWidth}
          validation={field.validation}
          required={required}
        />
      )
    case 'checkbox':
      return (
        <CheckboxInput
          id={inputId}
          value={value}
          label={field.label}
          onChange={onChange}
          disabled={disabled || field.disabled}
          required={required}
        />
      )
    case 'checked':
      return (
        <CheckedTextInput
          id={inputId}
          value={value}
          label={field.label}
          onChange={onChange}
          disabled={disabled}
          required={required}
        />
      )
    case 'color':
      return <ColorInput id={inputId} onChange={onChange} defaultColor={value} />
    case 'file_upload':
      return <S3FileUpload id={inputId} />
    case 'sectionTitle':
      return (
        <Typography variant="h6" fontWeight={600}>
          {field.label}
        </Typography>
      )
    case 'placeholder':
      return <div />
    default:
      return null
  }
}
